import React, { useEffect, useState } from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';

import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useLocation } from 'react-router-dom';
import SubscribeModal from './SubscribeModal/SubscribeModal';
import EnTetePage from '../../Layout/EnTetePage/EnTetePage';
import Notifications from './Notifications/Notifications';
import Bibliotheque from './Bibliotheque/Bibliotheque';
import { displayAlert, setLoading } from '../../../slices/feedbackSlice';
import { exportBooks, getCollections } from '../../../slices/itemsSlice';

// Les seuls segments d'adresse qui désignent vraiment un retour de paiement.
const STATUTS_DE_PAIEMENT = ['success', 'error'];

const useStyles = makeStyles()(() => ({
  root: {
    margin: 'auto',
    width: '99%',
    padding: '20px',
  },
}));

export default function MyAccount() {
  const { classes } = useStyles();
  const { t } = useTranslation('myAccount');

  const organization = useSelector((state) => state.organization.organization);
  // La collection sert au renommage : c'est SON nom qui titre la vitrine
  // publique. On prend la première — le produit n'en gère qu'une par
  // organisation aujourd'hui, même si le modèle en permet plusieurs.
  const collections = useSelector((state) => state.items.collections);
  const subscription = useSelector((state) => state.organization.subscription);
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);

  const location = useLocation().pathname;
  const paymentStatus = location.split('/').pop();

  const dispatch = useDispatch();

  // ⚠️ « Mon compte » ne chargeait pas les collections : sans cet appel, le
  // champ de renommage n'apparaîtrait que si l'on est passé par « Livres »
  // avant — donc jamais pour quelqu'un qui arrive par le menu.
  useEffect(() => {
    dispatch(getCollections());
  }, [dispatch]);

  // L'export rend la collection en classeur, dans un format que notre propre
  // import sait relire. C'est ce qui rend « vos données vous appartiennent »
  // vrai plutôt que décoratif.
  const handleExport = () => {
    dispatch(setLoading(true));
    dispatch(exportBooks())
      .unwrap()
      .then(({ blob, filename }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Sans révocation, le classeur reste en mémoire tant que l'onglet est
        // ouvert — plusieurs mégaoctets par export sur une grosse collection.
        window.URL.revokeObjectURL(url);
        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  };

  useEffect(() => {
    // ⚠️ Seulement les deux statuts qui existent. `paymentStatus` n'est que le
    // dernier segment de l'adresse : sur « /account » il vaut « account », et
    // l'alerte affichait alors la clé brute « payment_status_account » en bas
    // de l'écran de compte, à chaque visite.
    if (STATUTS_DE_PAIEMENT.includes(paymentStatus)) {
      dispatch(displayAlert(t(`payment_status_${paymentStatus}`), paymentStatus));
    }
  }, [t, dispatch, paymentStatus]);

  const handleSubscribeModalClose = () => {
    setSubscribeModalOpen(false);
  };

  const handleSubscribeModalOpen = () => {
    setSubscribeModalOpen(true);
  };
  const subscriptionDisplay = (organization.is_subscribed || paymentStatus === 'success') ? (
    <>
      <Grid item xs={12}>
        {/* ⚠️ « subtitle2 » et non « overline » : dans ce thème, overline vaut
            11 px et met le texte en capitales. C'est le nom du plan auquel
            l'organisme est abonné — la seule information de la carte — et il
            était plus petit que tout le reste de la page. */}
        <Typography component="p" variant="subtitle2">
          {t(`plans.${subscription.interval}.name`)}
        </Typography>
      </Grid>
      {/* <Grid item xs={12}> */}
      {/*  <Button */}
      {/*    variant="contained" */}
      {/*    component="label" */}
      {/*    color="secondary" */}
      {/*    onClick={handleSubscribeModalOpen} */}
      {/*  > */}
      {/*    {t('manage_subscription')} */}
      {/*  </Button> */}
      {/* </Grid> */}
    </>
  )
    : (
      <>
        <Grid item xs={12}>
          <Typography component="p" variant="subtitle2" color="error">
            {t('unsubscribed')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography component="p" variant="body1">
            {t('please_subscribe')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            component="label"
            color="secondary"
            onClick={handleSubscribeModalOpen}
          >
            {t('subscribe')}
          </Button>
        </Grid>
      </>
    );

  return (
    <>
      <SubscribeModal display={subscribeModalOpen} handleClose={handleSubscribeModalClose} />
      <Grid container spacing={2} className={classes.root}>
        <Grid item xs={12}>
          {/* L'écran n'avait AUCUN titre : la page s'ouvrait sur une phrase en
              14 px, seule, alignée nulle part. Elle passait pour une note
              égarée plutôt que pour l'introduction de l'écran. */}
          <EnTetePage titre={t('page_title')} intro={t('intro')} />
        </Grid>

        {/* 🔑 Deux colonnes, et « Notifications » à GAUCHE.
            C'est la carte la plus haute et la seule où l'on agit : la mettre en
            premier évite qu'on la cherche, et évite surtout le vide qu'elle
            laissait à côté d'une carte deux fois plus courte. Les deux cartes
            de droite, empilées, occupent à peu près sa hauteur.

            ⚠️ `organization.id` est indispensable pour écrire : sans lui, la
            carte s'afficherait et chaque commutateur échouerait. Tant que
            l'organisation n'est pas chargée, on n'affiche rien plutôt qu'une
            carte qui ne marche pas. */}
        {/* 🔑 « Votre bibliothèque » EN PREMIER, avant les notifications.
            Les deux noms qu'elle porte sont publics — vitrine et courriels —
            et aucun écran ne permettait de les changer : vingt-deux
            bibliothèques sur trente et une gardaient le nom généré à
            l'inscription, qui contenait l'adresse de leur direction. */}
        {organization.id && (
        <Grid item xs={12} lg={6}>
          <Bibliotheque organization={organization} collection={collections[0]} />
        </Grid>
        )}

        {organization.id && (
        <Grid item xs={12} lg={6}>
          <Notifications organization={organization} />
        </Grid>
        )}

        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            {/* Vos données.
                🔑 L'export vit ici et non dans « Livres » : c'est un geste de
                compte, pas de catalogue. On l'exporte une fois par an ou le
                jour où l'on part — pas en cherchant un titre. Le placer parmi
                les boutons d'action de la collection lui donnait le même poids
                qu'« Ajouter un livre », qu'on utilise tous les jours. */}
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title={t('data.title')}
                  titleTypographyProps={{ variant: 'h3' }}
                />
                <CardContent>
                  <Typography component="p" variant="body1" gutterBottom>
                    {t('data.description')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleExport}
                    // Le bouton touchait le texte au-dessus : rien ne séparait
                    // l'explication de l'action, et les deux se lisaient comme
                    // un seul bloc.
                    sx={{ mt: 2 }}
                  >
                    {t('data.button')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title={t('subscription')}
                  titleTypographyProps={{ variant: 'h3' }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    {subscriptionDisplay}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

      </Grid>
    </>
  );
}
